import React, { useState } from 'react';
import { LedgerEntryType } from '../clients/rust-api-client';
import { Button } from '../common/Catalyst/button';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { ledgerEntryType } from './enumLabels';

interface Props {
  value: LedgerEntryType[];
  onChange: (next: LedgerEntryType[]) => void;
  disabled?: boolean;
}

/**
 * The ordered list of entry types a payment is applied to.
 *
 * Two things this control has to say out loud:
 *
 * **An empty list is the shipped default, not an unconfigured field.**
 * It means oldest charge first regardless of type, which is a real and
 * common policy — several states mandate it, and others mandate the
 * opposite. A blank control that looks broken invites someone to
 * "fix" it into a rule their state does not allow.
 *
 * **Only charges can be ordered.** Application walks open charges, so a
 * credit type in this list would never match anything. Offering the
 * whole enum would be offering config that silently does nothing.
 *
 * Up/down rather than drag: Catalyst has no drag primitive, and a list
 * of at most ten rows edited once a year does not justify the
 * dependency.
 */
const PaymentOrderField: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [toAdd, setToAdd] = useState('');

  const available = ledgerEntryType
    .charges()
    .filter((t) => value.indexOf(t as LedgerEntryType) === -1);

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const remove = (index: number): void =>
    onChange(value.filter((_, i) => i !== index));

  const add = (): void => {
    if (!toAdd) return;
    onChange([...value, toAdd as LedgerEntryType]);
    setToAdd('');
  };

  return (
    <div className="mt-2">
      {value.length === 0 ? (
        <Text className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 p-3">
          <strong>Oldest charge first, whatever its type.</strong> That is this
          association&rsquo;s policy right now — an empty list is a setting, not
          a gap. Add a type below only if a rule says payments must go to one
          kind of charge before another.
        </Text>
      ) : (
        <ol className="space-y-2">
          {value.map((entry, i) => (
            <li
              key={entry}
              className="flex items-center gap-2 rounded-md border border-zinc-200 dark:border-zinc-700 px-3 py-2"
            >
              <span className="w-6 text-sm text-zinc-500 dark:text-zinc-400">
                {i + 1}.
              </span>
              <span className="flex-1 text-sm text-zinc-900 dark:text-white">
                {ledgerEntryType.label(entry)}
              </span>
              {!disabled && (
                <>
                  <Button
                    plain
                    aria-label={`Move ${ledgerEntryType.label(entry)} up`}
                    disabled={i === 0}
                    onClick={() => move(i, i - 1)}
                  >
                    ↑
                  </Button>
                  <Button
                    plain
                    aria-label={`Move ${ledgerEntryType.label(entry)} down`}
                    disabled={i === value.length - 1}
                    onClick={() => move(i, i + 1)}
                  >
                    ↓
                  </Button>
                  <Button
                    plain
                    aria-label={`Remove ${ledgerEntryType.label(entry)}`}
                    onClick={() => remove(i)}
                  >
                    Remove
                  </Button>
                </>
              )}
            </li>
          ))}
        </ol>
      )}

      {value.length > 0 && (
        <Text className="mt-2">
          Anything not listed is applied after these, oldest first.
        </Text>
      )}

      {!disabled && available.length > 0 && (
        <div className="mt-3 flex items-end gap-2">
          <FieldPicker
            label="Charge type to add"
            placeholder="Add a charge type…"
            value={toAdd}
            onChange={setToAdd}
            className="w-full max-w-xs"
            options={available.map((t) => ({
              value: t,
              label: ledgerEntryType.label(t),
            }))}
          />
          <Button color="sky" onClick={add} disabled={!toAdd}>
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaymentOrderField;
